import React, { useState, useEffect } from 'react';
import { Drawer, List, ListItem, ListItemButton, ListItemText, IconButton, Typography, TextField, Box, Divider, Tooltip, tooltipClasses, CircularProgress } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material/styles';
import { API, graphqlOperation } from "aws-amplify";
import { listSFTPSummaries, listSFTPMetadataSummaries } from "../../graphql_ap/queries";
import { styled } from '@mui/material/styles';
import { logUserEvent } from '../User/UserFunctions';


const drawerWidth = 270;

const SFTPSidebar = ({onSelectSFTPCategoryID}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true); // sidebar is open by default
  const [selectedItem, setSelectedItem] = useState(null); // State to track the selected item
  const [searchInput, setSearchInput] = useState(""); // State for search input
  const [SFTPCategories, setSFTPCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metadataLoaded, setMetadataLoaded] = useState(false);

  // Sort categories by name
  const sortedSFTPCategories = SFTPCategories.length > 0 
    ? [...SFTPCategories].sort((a, b) => a.SFTPName.localeCompare(b.SFTPName))
    : [];

  // Initial data fetch - first tier (faster)
  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        setLoading(true);
        const summariesResult = await API.graphql(graphqlOperation(listSFTPSummaries));
        
        if (summariesResult.data && summariesResult.data.listSFTPSummaries) {
          // Log the summaries for debugging
          console.log("Summaries result:", summariesResult.data.listSFTPSummaries);
          
          // Map the GraphQL data to the format expected by the component
          const summariesData = summariesResult.data.listSFTPSummaries.map(item => ({
            SFTPItemID: item.sftp_item_id,
            SFTPName: item.sftp_item_name,
            SFTPPythonCodeExample: item.sftp_python_code_example,
            SFTPNodeCodeExample: item.sftp_nodejs_code_example,
            SFTPSummary: item.sftp_summary,
            SFTPColumns: item.sftp_columns || [],
            SFTPGraphic: item.sftp_graphic,
            // Store the s3 category for matching with metadata
            SFTPS3Category: item.sftp_s3_category || item.sftp_cognito_group_name,
            // Initialize with empty files object that will be populated later
            SFTPFiles: { root: [] }
          }));
          
          setSFTPCategories(summariesData);
          setLoading(false);
          
          // Now fetch the metadata (second tier - slower)
          fetchMetadata();
        } else {
          console.error("Received data is not in the expected format", summariesResult);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching SFTP summaries:", error);
        setLoading(false);
      }
    };
    
    fetchSummaries();
  }, []);

  // Second tier data fetch (slower) - metadata
  const fetchMetadata = async () => {
    try {
      const metadataResult = await API.graphql(graphqlOperation(listSFTPMetadataSummaries));
      
      if (metadataResult.data && metadataResult.data.listSFTPMetadataSummaries) {
        // Log the metadata for debugging
        console.log("Metadata result:", metadataResult.data.listSFTPMetadataSummaries);
        
        // Update the existing categories with file metadata
        setSFTPCategories(prevCategories => {
          const updatedCategories = [...prevCategories];
          
          // Log all available categories and their S3 categories for debugging
          console.log("Available categories:", updatedCategories.map(cat => ({
            name: cat.SFTPName,
            s3Category: cat.SFTPS3Category
          })));
          
          metadataResult.data.listSFTPMetadataSummaries.forEach(metadata => {
            console.log("Processing metadata for:", metadata.sftp_s3_category);
            
            // Find the matching category by s3 category
            let categoryIndex = updatedCategories.findIndex(
              cat => cat.SFTPS3Category && 
                    cat.SFTPS3Category.toLowerCase() === metadata.sftp_s3_category.toLowerCase()
            );
            
            // If not found by s3 category, try matching by name
            if (categoryIndex === -1) {
              categoryIndex = updatedCategories.findIndex(
                cat => cat.SFTPName.toLowerCase() === metadata.sftp_s3_category.toLowerCase()
              );
            }
            
            // If still not found, try matching by ID if available
            if (categoryIndex === -1 && metadata.sftp_item_id) {
              categoryIndex = updatedCategories.findIndex(
                cat => cat.SFTPItemID === metadata.sftp_item_id
              );
            }
            
            if (categoryIndex !== -1) {
              // Ensure the files data is in the expected format
              let filesData = {};
              
              // Check if metadata.sftp_files exists and is an array
              if (metadata.sftp_files && Array.isArray(metadata.sftp_files)) {
                // Process each group in the sftp_files array
                metadata.sftp_files.forEach(group => {
                  if (group.groupName && Array.isArray(group.files)) {
                    // Add each group's files to the filesData object
                    filesData[group.groupName] = group.files;
                  }
                });
                
                // If no groups were found with groupName, try to use the first item's files
                if (Object.keys(filesData).length === 0 && metadata.sftp_files.length > 0 && metadata.sftp_files[0].files) {
                  filesData.root = metadata.sftp_files[0].files;
                }
              }
              
              // If we still don't have any files data, check if there's a files property directly on the metadata
              if (Object.keys(filesData).length === 0 && metadata.files && Array.isArray(metadata.files)) {
                filesData.root = metadata.files;
              }
              
              // Ensure there's at least an empty root array
              if (Object.keys(filesData).length === 0 || !filesData.root) {
                filesData.root = [];
              }
              
              console.log(`Processed files data for ${metadata.sftp_s3_category}:`, filesData);
              
              // Update the files data for this category
              updatedCategories[categoryIndex] = {
                ...updatedCategories[categoryIndex],
                SFTPFiles: filesData
              };
              
              console.log(`Updated files data for ${updatedCategories[categoryIndex].SFTPName}:`, filesData);
            } else {
              console.warn(`No matching category found for metadata: ${metadata.sftp_s3_category}`);
            }
          });
          
          return updatedCategories;
        });
        
        setMetadataLoaded(true);
      } else {
        console.error("Received metadata is not in the expected format", metadataResult);
      }
    } catch (error) {
      console.error("Error fetching SFTP metadata:", error);
    }
  };

  const [SFTPItems, setSFTPItems] = useState([]);
  
  // Update SFTPItems when categories change
  useEffect(() => {
    if (SFTPCategories.length > 0) {
      setSFTPItems(sortedSFTPCategories);
    }
  }, [SFTPCategories]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleDrawerOpen = () => { // used to be able to open the sidebar by clicking anywhere
    if (!open) setOpen(true);
  };

  const handleListItemClick = (item) => {
    setSelectedItem(item.SFTPItemID); // Update the selected item
    // logUserEvent('sftp-select', item.SFTPName);
    onSelectSFTPCategoryID(
      item.SFTPItemID, 
      item.SFTPName, 
      item.SFTPFiles,
      item.SFTPPythonCodeExample,
      item.SFTPNodeCodeExample,
      item.SFTPSummary,
      item.SFTPColumns
    ); // Pass all needed data to parent component
  };

  const handleFilterChange = (event) => {
    setSearchInput(event.target.value);
  };

  const filteredSFTPItems = SFTPItems.filter(
    (item) => item.SFTPName.toLowerCase().includes(searchInput.toLowerCase())
  );

  return (
    <Drawer
      variant="permanent"
      open={open}
      onClick={handleDrawerOpen} // top open the sidebar by clicking anywhere
      sx={{
        width: open ? drawerWidth : theme.spacing(7) + 1,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : theme.spacing(7) + 1,
          boxSizing: 'border-box',
          height: 'calc(100vh - 62px - 24px - 2px)',
          overflow: 'hidden',
          top: 'inherit',
          position: 'relative',
          zIndex: 'auto',
          ...(open ? {} : {
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }),
        },
      }}
    >
      {open && (
        <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'left', fontWeight: 'bold', paddingLeft: '10px' }}>
            Data Sets
          </Typography>
          <IconButton onClick={handleDrawerToggle}>
            {theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}
      <Divider />
      {!open && (
        <IconButton onClick={handleDrawerToggle} sx={{ width: '100%' }}>
          <MenuIcon />
        </IconButton>
      )}
      {open && <TextField
        id="outlined-search"
        label="search"
        type="search"
        size="small"
        autoFocus // Automatically focus this field
        value={searchInput}
        onChange={handleFilterChange}
        style={{ margin: '10px', marginTop: '20px', mx: 2 }}
      />}
      {open && (
        loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ 
            overflow: 'auto', 
            height: 'calc(100vh - 62px - 24px - 2px - 150px)', 
            pl: 2, 
            '& .MuiListItemButton-root': {     // Reduces vertical padding
              py: 0.5,                         // Reduces vertical padding
            },                                 // Reduces vertical padding
            '& .MuiListItemText-primary': {    // Smaller text size for list items
              fontSize: '0.875rem',            // Smaller text size for list items
            },                                 // Smaller text size for list items
            '&::-webkit-scrollbar': {
              width: '0.4em',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '4px',
            },
            '&:hover::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
          >
            {filteredSFTPItems.map((item) => ( 
              <ListItem key={item.SFTPItemID} disablePadding>
                <ListItemButton onClick={() => handleListItemClick(item)}>
                  <ListItemText 
                    primary={item.SFTPName}
                    primaryTypographyProps={{
                      fontWeight: selectedItem === item.SFTPItemID ? 'bold' : 'normal', // Bold the text if it's the selected item
                      style: {
                        color: selectedItem === item.SFTPItemID ? theme.palette.ke.link_hover : 'inherit',
                      },
                    }}
                  />
                  {!metadataLoaded && selectedItem === item.SFTPItemID && (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                  )}
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )
      )}
    </Drawer>
  );
};

export default SFTPSidebar;
